import React, {useEffect, useMemo, useState, useCallback} from "react";
import {
    Table, Card, Typography, message, Row, Col, Button,
    Tabs, Select, Modal, Input, Spin
} from "antd";
import {PlusOutlined} from "@ant-design/icons";
import {useNavigate} from "react-router-dom";
import api from "../../api";

const {Title} = Typography;
const {TabPane} = Tabs;

type Status = "Please Ask" | "Interested" | "Not-Interested" | "Not Responding";
type TabKey = "All" | Status;

const STATUS_OPTIONS: Status[] = ["Please Ask", "Interested", "Not-Interested", "Not Responding"];

type TargetRow = {
    id: number;
    status: Status | null;
    customer_id: number;
    first_name: string;
    last_name: string;
    email?: string | null;
    phone?: string | null;
};

const ENDPOINTS = {
    list: "/daia/targets/",
    create: "/daia/targets/",
    update: (id: number) => `/daia/targets/${id}/`,
};

const TargetsPage: React.FC = () => {
    const navigate = useNavigate();

    const [rows, setRows] = useState<TargetRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
    const [activeTab, setActiveTab] = useState<TabKey>("Please Ask");
    const [pagination, setPagination] = useState({current: 1, pageSize: 10, total: 0});

    const [addOpen, setAddOpen] = useState(false);
    const [addSubmitting, setAddSubmitting] = useState(false);
    const [mmid, setMmid] = useState("");

    const fetchTargets = useCallback(async (page = pagination.current, pageSize = pagination.pageSize) => {
        setLoading(true);
        try {
            const params: any = {page, page_size: pageSize};
            // ⬅ Only send status if not "All"
            if (activeTab !== "All") params.status = activeTab;

            const res = await api.get(ENDPOINTS.list, {params});
            const results: TargetRow[] = Array.isArray(res.data) ? res.data : res.data.results ?? [];
            const count = res.data?.count ?? results.length;

            setRows(results);
            setPagination((p) => ({...p, total: count}));
        } catch (e) {
            console.error(e);
            message.error("Failed to load targets.");
        } finally {
            setLoading(false);
        }
    }, [activeTab, pagination.current, pagination.pageSize]);

    useEffect(() => {
        fetchTargets();
    }, [fetchTargets]);

    // Reset to page 1 when tab changes
    useEffect(() => {
        setPagination((p) => ({...p, current: 1}));
    }, [activeTab]);

    const handleStatusChange = async (id: number, status: Status) => {
        const prev = [...rows];
        setRows((rs) => rs.map((r) => (r.id === id ? {...r, status} : r)));
        setSavingIds((s) => new Set(s).add(id));
        try {
            await api.patch(ENDPOINTS.update(id), {status});
            message.success("Status updated.");
        } catch (e) {
            console.error(e);
            setRows(prev);
            message.error("Failed to update status.");
        } finally {
            setSavingIds((s) => {
                const ns = new Set(s);
                ns.delete(id);
                return ns;
            });
        }
    };

    const columns = useMemo(
        () => [
            {
                title: "Customer",
                render: (_: any, rec: TargetRow) =>
                    `${rec.first_name ?? ""} ${rec.last_name ?? ""}`.trim() || "-",
            },
            {title: "Email", dataIndex: "email", render: (v: string | null | undefined) => v || "-"},
            {title: "Phone", dataIndex: "phone", render: (v: string | null | undefined) => v || "-"},
            {
                title: "Status",
                render: (_: any, rec: TargetRow) => {
                    const saving = savingIds.has(rec.id);
                    return (
                        <div onClick={(e) => e.stopPropagation()}>
                            <Select
                                value={rec.status ?? undefined}
                                placeholder="Set status"
                                style={{minWidth: 200}}
                                onChange={(val) => handleStatusChange(rec.id, val as Status)}
                                disabled={saving}
                                options={STATUS_OPTIONS.map((s) => ({value: s, label: s}))}
                            />
                            {saving && <Spin size="small" style={{marginLeft: 8}}/>}
                        </div>
                    );
                },
            },
        ],
        [savingIds]
    );

    const onAdd = async () => {
        setAddSubmitting(true);
        try {
            const trimmed = mmid.trim();
            if (!trimmed) {
                message.warning("Please enter an MMID.");
                setAddSubmitting(false);
                return;
            }
            await api.post(ENDPOINTS.create, {mmid: trimmed});
            message.success("Target added.");
            setAddOpen(false);
            setMmid("");
            // refresh current page
            fetchTargets();
        } catch (e: any) {
            console.error(e);
            const detail =
                e?.response?.data?.mmid || e?.response?.data?.detail || "Add failed.";
            message.error(detail);
        } finally {
            setAddSubmitting(false);
        }
    };

    return (
        <Card
            className="rounded-2xl shadow-sm"
            title={
                <Row justify="space-between" align="middle">
                    <Col>
                        <Title level={3} style={{margin: 0}}>Targets</Title>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            icon={<PlusOutlined/>}
                            className="rounded-md"
                            onClick={() => setAddOpen(true)}
                        >
                            Add
                        </Button>
                    </Col>
                </Row>
            }
        >
            <Tabs activeKey={activeTab} onChange={(k) => setActiveTab(k as TabKey)}>
                <TabPane tab="Please Ask" key="Please Ask"/>
                <TabPane tab="Interested" key="Interested"/>
                <TabPane tab="Not-Interested" key="Not-Interested"/>
                <TabPane tab="Not Responding" key="Not Responding"/>
                <TabPane tab="All" key="All"/>
            </Tabs>

            <Table
                rowKey="id"
                loading={loading}
                dataSource={rows}
                columns={columns as any}
                pagination={{
                    ...pagination,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => setPagination((p) => ({...p, current: page, pageSize})),
                    onShowSizeChange: (_cur, size) => setPagination((p) => ({...p, current: 1, pageSize: size})),
                    showTotal: (total, [start, end]) => `${start}-${end} of ${total}`,
                }}
                onRow={(rec: TargetRow) => ({
                    onClick: () => navigate(`/cc/customers/${rec.customer_id}`),
                    style: {cursor: "pointer"},
                })}
                className="rounded-xl overflow-hidden"
            />

            <Modal
                title="Add Target"
                open={addOpen}
                onCancel={() => {
                    setAddOpen(false);
                    setMmid("");
                }}
                onOk={onAdd}
                okButtonProps={{loading: addSubmitting, className: "rounded-md"}}
                cancelButtonProps={{className: "rounded-md"}}
                destroyOnClose
            >
                <p>Enter MMID for the customer to add:</p>
                <Input
                    placeholder="MMID"
                    value={mmid}
                    onChange={(e) => setMmid(e.target.value)}
                    onPressEnter={onAdd}
                    autoFocus
                />
            </Modal>
        </Card>
    );
};

export default TargetsPage;
